import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Box, Upload, Trash2, Loader2, Send, CheckCircle2, XCircle, Eye, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

export default function ForgeViewerPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewingModel, setViewingModel] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgResp, modelsResp] = await Promise.all([
        axios.get(`${API}/admin/forge-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/forge/models`, { headers: getAuthHeaders() }),
      ]);
      setConfig(cfgResp.data);
      setModels(modelsResp.data || []);
    } catch { toast.error("Failed to load Forge config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/forge-config`, config, { headers: getAuthHeaders() });
      toast.success("Forge configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const resp = await axios.post(`${API}/admin/forge-test`, {}, { headers: getAuthHeaders() });
      setTestResult(resp.data);
      if (resp.data.status === "connected") toast.success("Authentication successful");
      else toast.warning(resp.data.message);
    } catch (err) { setTestResult({ status: "error", message: err.response?.data?.detail || "Test failed" }); }
    finally { setTesting(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axios.post(`${API}/forge/upload-dwg`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded — translation started`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDelete = async (modelId) => {
    if (!window.confirm("Delete this model?")) return;
    try {
      await axios.delete(`${API}/forge/models/${modelId}`, { headers: getAuthHeaders() });
      toast.success("Model deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const checkStatus = async (modelId) => {
    try {
      const resp = await axios.get(`${API}/forge/models/${modelId}/status`, { headers: getAuthHeaders() });
      setModels(prev => prev.map(m => m.id === modelId ? resp.data : m));
      if (resp.data.status === "ready") toast.success("Model ready for viewing");
    } catch { toast.error("Status check failed"); }
  };

  const updateConfig = (k, v) => setConfig(p => ({ ...p, [k]: v }));

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#64748B]" size={24} /></div></Layout>;

  const isConfigured = config?.client_id && config?.client_secret !== "••••••••" && config?.client_secret;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="forge-viewer-page">
        <PageHeader title="DWG Native Viewer" subtitle="Autodesk Platform Services (Forge) — native DWG/RVT/IFC rendering in browser" testId="forge-title" />

        {/* Config */}
        <div className="bg-white border border-[#E2E8F0] p-6" data-testid="forge-config-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2"><Box size={20} className="text-[#2563EB]" /> Autodesk Credentials</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing || !config?.client_id} className="rounded-none" data-testid="forge-test-btn">
                {testing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Test Auth
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-none bg-[#2563EB] text-white" data-testid="forge-save-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Client ID</Label>
              <Input value={config?.client_id || ""} onChange={e => updateConfig("client_id", e.target.value)} placeholder="Your Autodesk App Client ID" className="mt-1 rounded-none font-mono text-xs" data-testid="forge-client-id" />
            </div>
            <div>
              <Label className="text-sm font-medium">Client Secret</Label>
              <Input type="password" value={config?.client_secret || ""} onChange={e => updateConfig("client_secret", e.target.value)} className="mt-1 rounded-none font-mono text-xs" data-testid="forge-client-secret" />
            </div>
            <div>
              <Label className="text-sm font-medium">Region</Label>
              <select value={config?.region || "US"} onChange={e => updateConfig("region", e.target.value)} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" data-testid="forge-region">
                <option value="US">US</option>
                <option value="EMEA">EMEA (Europe)</option>
              </select>
            </div>
          </div>
          {testResult && (
            <div className={`mt-4 p-3 border ${testResult.status === "connected" ? "border-[#10B981] bg-[#F0FDF4]" : "border-[#DC2626] bg-[#FEF2F2]"}`} data-testid="forge-test-result">
              <div className="flex items-center gap-2">
                {testResult.status === "connected" ? <CheckCircle2 size={16} className="text-[#10B981]" /> : <XCircle size={16} className="text-[#DC2626]" />}
                <span className="text-sm font-medium">{testResult.message}</span>
              </div>
            </div>
          )}
          <div className="mt-3 bg-[#F0F9FF] border border-[#BAE6FD] p-3">
            <p className="text-xs text-[#0369A1]">Get credentials at <a href="https://aps.autodesk.com" target="_blank" rel="noreferrer" className="underline font-bold">aps.autodesk.com</a> → Create App → Select "Model Derivative API" + "Data Management API"</p>
          </div>
        </div>

        {/* Upload & Models */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="forge-models-panel">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
            <h2 className="font-bold text-lg text-[#0F172A]">Uploaded Models ({models.length})</h2>
            <div>
              <input type="file" accept=".dwg,.dxf,.rvt,.ifc,.stp,.step" onChange={handleUpload} className="hidden" id="forge-upload" />
              <label htmlFor="forge-upload">
                <Button as="span" disabled={uploading} className="rounded-none bg-[#D97706] text-white cursor-pointer" data-testid="forge-upload-btn" onClick={() => document.getElementById("forge-upload").click()}>
                  {uploading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
                  {uploading ? "Uploading..." : "Upload DWG/RVT/IFC"}
                </Button>
              </label>
            </div>
          </div>
          {models.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#94A3B8]">
              <Box size={32} className="mx-auto mb-2 text-[#CBD5E1]" />
              No models uploaded yet. Upload a DWG, RVT, IFC, or STEP file to view in browser.
            </div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {models.map(m => (
                <div key={m.id} className="p-4 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`forge-model-${m.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-[#0F172A]">{m.filename}</span>
                      <span className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider border ${
                        m.status === "ready" ? "border-[#10B981] text-[#10B981] bg-[#F0FDF4]" :
                        m.status === "failed" ? "border-[#DC2626] text-[#DC2626] bg-[#FEF2F2]" :
                        "border-[#F59E0B] text-[#92400E] bg-[#FEF3C7]"
                      }`}>{m.status}</span>
                      {m.progress && m.status === "translating" && <span className="text-xs text-[#64748B]">{m.progress}</span>}
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">By {m.uploaded_by_name} · {new Date(m.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1">
                    {m.status === "translating" && (
                      <Button size="sm" variant="outline" onClick={() => checkStatus(m.id)} className="rounded-none" data-testid={`check-status-${m.id}`}><RefreshCw size={14} /></Button>
                    )}
                    {m.status === "ready" && (
                      <Button size="sm" onClick={() => setViewingModel(m)} className="rounded-none bg-[#2563EB] text-white" data-testid={`view-model-${m.id}`}><Eye size={14} className="mr-1" /> View</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleDelete(m.id)} className="rounded-none border-[#DC2626] text-[#DC2626]" data-testid={`delete-model-${m.id}`}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Viewer Placeholder */}
        {viewingModel && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="forge-viewer-container">
            <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h3 className="font-bold text-sm text-[#0F172A]">Viewing: {viewingModel.filename}</h3>
              <Button variant="outline" size="sm" onClick={() => setViewingModel(null)} className="rounded-none">Close</Button>
            </div>
            <div className="h-[500px] flex items-center justify-center bg-[#1E293B]">
              <div className="text-center">
                <Box size={48} className="mx-auto text-[#64748B] mb-3" />
                <p className="text-white font-bold">Autodesk Viewer</p>
                <p className="text-[#94A3B8] text-sm mt-1">URN: {viewingModel.urn?.slice(0, 30)}...</p>
                <p className="text-[#64748B] text-xs mt-2">The Autodesk Viewer JS library loads from aps.autodesk.com CDN.<br />Enter your credentials above and redeploy to enable live viewing.</p>
              </div>
            </div>
          </div>
        )}

        {/* Supported Formats */}
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4">
          <h3 className="font-bold text-sm text-[#0F172A] mb-2">Supported Formats</h3>
          <div className="flex flex-wrap gap-2">
            {["DWG", "DXF", "RVT", "IFC", "STP", "STEP", "SLDPRT", "IPT", "CATPART", "NWD", "3DS", "FBX", "OBJ"].map(f => (
              <span key={f} className="text-[10px] px-2 py-1 bg-white border border-[#E2E8F0] font-mono font-bold text-[#64748B]">.{f.toLowerCase()}</span>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
